import { FieldArrayWithId, UseFieldArrayInsert, UseFormReturn } from "react-hook-form"
import { CategoriesPrivate, SettingsNavigationService, SettingsNavigationsUpdate, SettingsNavigationUpdate } from "../client"
import { DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { Dispatch, SetStateAction } from "react"

/* Interfaces */
export interface SettingsNavProps extends SettingsNavigationsUpdate {
  navs?: (SettingsNavigationUpdate & { id: string, file: File | null , file_path: string | null})[] | null
}

export interface SettingsNavPointsProps {
    block_num: number
    me_create: boolean
    me_read: boolean
    ct_read: boolean
    categories_list: CategoriesPrivate | undefined
    blocks: FieldArrayWithId<SettingsNavProps, "navs", "id">[]
    insert: UseFieldArrayInsert<SettingsNavProps, "navs">
    form: UseFormReturn<SettingsNavProps>
    openItems: string[]
    setOpenItems: Dispatch<SetStateAction<string[]>>
}

export interface SettingsNavPointProps {
    order_number: number
    parent_order_number: number | null
    me_create: boolean
    me_read: boolean
    ct_read: boolean
    categories_list: CategoriesPrivate | undefined
    children?: React.ReactNode
    form: UseFormReturn<SettingsNavProps>
}


/* Endpoints */
export const readSettingsNavigations = () => {
    return {
        queryFn: async () => {
            const data = await SettingsNavigationService.readSettingsNavigations()
            return data ?? { data: null, count: 0 }
        },
        queryKey: ["settings-navigation"],
    }
}

/* FUNCTIONS */
export const addBlock = (
    parentIndex: number | null,
    insert: UseFieldArrayInsert<SettingsNavProps, "navs">,
    form: UseFormReturn<SettingsNavProps>,
    openItem: Dispatch<SetStateAction<string[]>>,
) => {
    const { setValue, getValues } = form

    const navsBefore = getValues("navs") ?? []

    let insertIndex = navsBefore.length

    if (parentIndex !== null) {
        insertIndex = parentIndex + 1

        // Skip existing children
        while (
            insertIndex < navsBefore.length &&
            ((navsBefore[insertIndex].parent_order_number ?? 0) - 1) === parentIndex
        ) {
            insertIndex++
        }
    }

    const parent_order = parentIndex != null ? parentIndex + 1 : 0
    const newId = crypto.randomUUID()
    insert(insertIndex, {
        id: newId,
        title: "",
        path: "",
        custom_view: "first-big-all-img",
        is_active: true,
        order_number: navsBefore.length + 1,
        parent_order_number: parent_order,
        fk_media_id: null,
        fk_category_id: "",
        file: null,
        file_path: null,
    })

    // Set open state
    openItem(prev => [...prev, newId])

    // Re-read after insert
    const navsAfter = getValues("navs") ?? []

    const reindexed = navsAfter.map((item, index) => {
        let newParent = item.parent_order_number ?? 0

        if (newParent > 0) {
            if ((newParent - 1) >= insertIndex) {
                newParent = newParent + 1
            }
        }

        return {
            ...item,
            order_number: index + 1,
            parent_order_number: newParent
        }
    })

    setValue("navs", reindexed, { shouldDirty: true })
}


export const removeBlock = (
    id: string,
    form: UseFormReturn<SettingsNavProps>
): void => {
    const { setValue, getValues } = form

    const navs = getValues("navs") ?? []

    // Find index of the block to remove
    const removeIndex = navs.findIndex(n => n.id === id)
    if (removeIndex === -1) return

    // Remove the block AND its children
    const filtered = navs.filter((item, index) => {
        if (index === removeIndex) return false
        if (item.parent_order_number === removeIndex + 1) return false
        return true
    })

    // How many items were removed? (1 parent + X children)
    const removedCount = navs.length - filtered.length

    // Reindex everything
    const reindexed = filtered.map((item, newIndex) => {
        let newParent = item.parent_order_number ?? 0

        if (newParent) {
            if (newParent > removeIndex + 1) newParent = newParent - removedCount
        }

        return {
            ...item,
            order_number: newIndex + 1,
            parent_order_number: newParent
        }
    })

    setValue("navs", reindexed, { shouldDirty: true })
}

export const onDragEnd = (
    event: DragEndEvent,
    form: UseFormReturn<SettingsNavProps>
) => {
    const { setValue, getValues, clearErrors, trigger } = form

    const { active, over } = event
    if (!over || active.id === over.id) return

    const navs = getValues("navs") ?? []

    const oldIndex = navs.findIndex(b => b.id === active.id)
    const newIndex = navs.findIndex(b => b.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const isParent = navs[oldIndex].parent_order_number == 0

    if (!isParent) {
        const reordered = arrayMove(navs, oldIndex, newIndex)

        const reindexed = reordered.map((b, i) => {
            let newParent: number = 0

            if (b.parent_order_number && b.parent_order_number > 0) {
                const parentId = navs[(b.parent_order_number - 1)].id
                newParent = reordered.findIndex(x => x.id === parentId) + 1
            }

            return {
                ...b,
                order_number: i + 1,
                parent_order_number: newParent,
            }
        })

        setValue("navs", reindexed, { shouldDirty: true })
        clearErrors("navs")
        trigger("navs")
        return
    }

    const parentIdMap = new Map<string, string | undefined>()
    navs.forEach(item => {
        if (item.parent_order_number && item.parent_order_number > 0) {
            const parentId = navs[(item.parent_order_number - 1)].id
            parentIdMap.set(item.id, parentId)
        }
    })

    const parentsWithIndex = navs
        .map((b, i) => ({ ...b, _index: i }))
        .filter(b => b.parent_order_number == 0)

    const oldParentPos = parentsWithIndex.findIndex(p => p.id === active.id)

    const overBlock = navs[newIndex]
    const overParentIndex = overBlock.parent_order_number == 0
        ? newIndex : ((overBlock.parent_order_number ?? 0) - 1)

    const overParentId = navs[overParentIndex].id
    const newParentPos = parentsWithIndex.findIndex(p => p.id === overParentId)

    const reorderedParents = arrayMove(parentsWithIndex, oldParentPos, newParentPos)

    const rebuilt: typeof navs = []
    reorderedParents.forEach(parent => {
        const parentPrevIndex = parent._index
        rebuilt.push(navs[parentPrevIndex])

        navs.forEach(b => {
            if (((b.parent_order_number ?? 0) - 1) === parentPrevIndex) {
                rebuilt.push(b)
            }
        })
    })

    const final = rebuilt.map((block, index) => {
        const parentId = parentIdMap.get(block.id)

        if (!parentId) {
            return {
                ...block,
                order_number: index + 1,
                parent_order_number: 0,
            }
        }

        const newParentIndex = rebuilt.findIndex(b => b.id === parentId)

        return {
            ...block,
            order_number: index + 1,
            parent_order_number: newParentIndex + 1,
        }
    })

    setValue("navs", final, { shouldDirty: true })
    clearErrors("navs")
    trigger("navs")
}
